import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDomain from '@/hooks/useDomain';
import { useDispatch, useSelector } from 'react-redux';
import { removeFilters } from '@/app/common/reducers/filterSlice';
import { stopShowing } from '@/app/common/reducers/helpersShowSlice';
import IconAlert from '@/components/common/IconAlert';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import ClusterViewModeSwitcher from '../keyword-clustring/components/ClusterViewModeSwitcher';

type RelatedKeywordsMode = 'auto' | 'manual';

type RelatedKeywordsModeToggleProps = {
  mode: RelatedKeywordsMode;
};

type HelpersShowRootState = {
  helpersShow: string[];
};

const RELATED_HELPER_ID = '/keyword-research/related';
const MASSIVE_HELPER_ID = '/keyword-research/massive';

const autoTooltipContent = (
  <>
    کلمات مرتبط به صورت خودکار (هر ۲۴ ساعت یک بار) و براساس کلماتی که در ابزار
    ردیاب کلمات اضافه کرده باشید، به‌روزرسانی می‌شود. برای افزایش تعداد کلمات
    مرتبط، تعداد کلمات در ردیاب و تاریخچه استفاده از آن ابزار مؤثر است.
  </>
);

const manualTooltipContent = (
  <>
    کلمات انبوه برای پیدا کردن کلمات کلیدی پرسرچ اخیر در نتایج گوگل، یاهو یا
    یوتیوب مناسب است. پیشنهاد می‌شود یک کلمه کلی وارد کنید تا کلمات طولانی
    پرسرچ اخیر نمایش داده شود.
  </>
);

const relatedHelperContent = (
  <>
    تب کلمات مرتبط به صورت خودکار (هر ۲۴ ساعت یک بار) و براساس کلماتی که در
    ابزار ردیاب کلمات اضافه کرده باشید، به‌روزرسانی میشه. برای افزایش تعداد
    کلمات مرتبط در این تب، دو عامل اثرگذار هست: تعداد کلمات در ابزار ردیاب
    کلمات و تاریخچه استفاده از آن ابزار. اطلاعات بیشتر درباره این تب در{' '}
    <a
      target="_blank"
      rel="noreferrer noopener"
      href="https://help.mizfa.tools/related-searches-google/"
    >
      این لینک
    </a>{' '}
    موجود هست.
  </>
);

const manualHelperContent = (
  <>
    کلمات انبوه برای پیدا کردن کلمات کلیدی پرسرچ اخیر در نتایج گوگل بسیار مناسب
    هست، منبع تمام کلمات پیدا شده نتایج گوگل، یاهو یا یوتیوب هست، پیشنهاد
    میشه برای گرفتن نتیجه بهتر یک کلمه کلی وارد کنید تا ابزار به شما کلمات
    طولانی پرسرچ اخیر رو نمایش بده.
  </>
);

const RelatedKeywordsModeToggle = ({ mode }: RelatedKeywordsModeToggleProps) => {
  const { domain_slug, domain } = useDomain();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const helpersShowState = useSelector(
    (state: HelpersShowRootState) => state.helpersShow
  );
  const [showHelper, setShowHelper] = useState(true);

  const helperId = mode === 'auto' ? RELATED_HELPER_ID : MASSIVE_HELPER_ID;
  const helperContent =
    mode === 'auto' ? relatedHelperContent : manualHelperContent;

  useEffect(() => {
    setShowHelper(true);
  }, [mode]);

  const handleChange = (value: string) => {
    if (value === mode) return;
    dispatch(removeFilters());
    if (value === 'auto') {
      navigate(`/keyword-research/related/${domain_slug}/${domain}`);
    } else {
      navigate(`/keyword-research/massive/${domain_slug}/${domain}`);
    }
  };

  return (
    <div className="d-flex flex-column align-items-center">
      <ClusterViewModeSwitcher
        className="mb-3"
        value={mode}
        onChange={handleChange}
        aria-label="حالت کلمات مرتبط"
        modes={[
          {
            id: 'manual',
            icon: 'list-ul',
            label: (
              <>
                دستی (انبوه)
                <HelpTooltip html={manualTooltipContent} placement="top" />
              </>
            )
          },
          {
            id: 'auto',
            icon: 'arrow-repeat',
            label: (
              <>
                خودکار
                <HelpTooltip html={autoTooltipContent} placement="top" />
              </>
            )
          }
        ]}
      />

      {!helpersShowState?.includes(helperId) && (
        <IconAlert
          show={showHelper}
          onClose={() => {
            setShowHelper(false);
            dispatch(stopShowing(helperId));
          }}
          variant="info"
          className="w-100"
          dismissible
        >
          <p className="mb-0 text-justify pe-3">{helperContent}</p>
        </IconAlert>
      )}
    </div>
  );
};

export default RelatedKeywordsModeToggle;
